import React, { useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Trash2, Star, Crop, Upload, GripVertical, Video as VideoIcon, Image as ImageIcon } from "lucide-react";
import { PHOTO_MIN, PHOTO_MAX } from "./constants";
import PhotoCropper from "./PhotoCropper";
import { toast } from "sonner";
import { resolveImageUrl as resolveUrl, onImageError } from "@/lib/img";

/**
 * Premium gallery editor.
 *  - Multi-select upload (image/video)
 *  - HTML5 drag-and-drop reordering (touch fallback via the "Make main" button)
 *  - Crop modal on each image
 *  - Per-upload progress, main-photo + cover-photo controls
 */
export default function GalleryEditor({ value = [], onChange }) {
  const inputRef = useRef(null);
  const dragIdx = useRef(null);
  const [uploads, setUploads] = useState({}); // {tmpId: { name, pct, error? }}
  const [cropping, setCropping] = useState(null); // { idx, src }

  const imageCount = value.filter(v => v.type === "image").length;
  const canAddImage = imageCount < PHOTO_MAX;

  const handleFiles = useCallback(async (files) => {
    if (!files?.length) return;
    // CRITICAL: use a LOCAL accumulator so parallel-looking sequential uploads
    // don't overwrite each other by all reading the same stale `value` from
    // closure. Each iteration must build the next state from the previous
    // iteration's result, not from the value captured when handleFiles was
    // invoked.
    let current = value;
    let currentImageCount = imageCount;
    for (const file of Array.from(files)) {
      const isImg = file.type.startsWith("image/");
      const isVid = file.type.startsWith("video/");
      if (!isImg && !isVid) { toast.error(`Unsupported: ${file.name}`); continue; }
      if (isImg && currentImageCount >= PHOTO_MAX) { toast.error(`Max ${PHOTO_MAX} photos`); break; }
      // Per-file size cap: 15MB for images, 50MB for videos.
      const maxBytes = isVid ? 50 * 1024 * 1024 : 15 * 1024 * 1024;
      if (file.size > maxBytes) {
        toast.error(`${file.name} exceeds ${isVid ? "50" : "15"}MB limit`);
        continue;
      }
      const tmpId = `t-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setUploads((u) => ({ ...u, [tmpId]: { name: file.name, pct: 0 } }));
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post(isVid ? "/uploads/video" : "/uploads/image", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            const pct = evt.total ? Math.round((evt.loaded * 100) / evt.total) : 0;
            setUploads((u) => ({ ...u, [tmpId]: { ...u[tmpId], pct } }));
          },
        });
        const next = [...current, {
          url: data.path,
          type: isVid ? "video" : "image",
          is_main: currentImageCount === 0 && isImg,
          position: current.length,
        }];
        current = next;
        if (isImg) currentImageCount += 1;
        onChange(next);
      } catch (e) {
        console.warn("[GalleryEditor] upload error:", e);
        toast.error(`Upload failed: ${file.name}`);
        setUploads((u) => ({ ...u, [tmpId]: { ...u[tmpId], error: true } }));
      } finally {
        setTimeout(() => setUploads((u) => { const n = { ...u }; delete n[tmpId]; return n; }), 600);
      }
    }
  }, [value, onChange, imageCount]);

  // Reordering
  const onDragStart = (i) => (e) => { dragIdx.current = i; e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const next = [...value];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    onChange(next.map((g, idx) => ({ ...g, position: idx })));
    dragIdx.current = null;
  };

  // Single-item ops
  const moveTo = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next.map((g, i) => ({ ...g, position: i })));
  };
  const setMain = (i) => onChange(value.map((g, idx) => ({ ...g, is_main: idx === i && g.type === "image" })));
  const remove  = (i) => {
    const removed = value[i];
    const next = value.filter((_, idx) => idx !== i);
    // If we deleted the main image, promote the first remaining image
    if (removed?.is_main) {
      const firstImg = next.findIndex(g => g.type === "image");
      if (firstImg >= 0) next[firstImg] = { ...next[firstImg], is_main: true };
    }
    onChange(next.map((g, idx) => ({ ...g, position: idx })));
  };

  // Crop flow — upload the cropped blob as a new image, replace URL in place
  const startCrop = async (i) => {
    const item = value[i];
    if (!item || item.type !== "image") return;
    try {
      const src = resolveUrl(item.url);
      // Fetch via blob so canvas tainting doesn't break export
      const resp = await fetch(src, { credentials: "include" });
      const blob = await resp.blob();
      setCropping({ idx: i, src: URL.createObjectURL(blob) });
    } catch (e) {
      console.warn("[crop] fetch image error", e);
      toast.error("Could not load image for cropping");
    }
  };

  const finishCrop = async (blob) => {
    const idx = cropping.idx;
    setCropping(null);
    const form = new FormData();
    form.append("file", new File([blob], "cropped.jpg", { type: "image/jpeg" }));
    try {
      const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const next = value.map((g, i) => i === idx ? { ...g, url: data.path } : g);
      onChange(next);
      toast.success("Photo cropped");
    } catch (e) {
      console.warn("[crop] upload error", e);
      toast.error("Crop save failed");
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload zone */}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-full border-2 border-dashed border-stone-700 hover:border-amber-500/60 rounded-xl py-7 sm:py-9 flex flex-col items-center justify-center gap-2 transition group"
        data-testid="gallery-upload-btn"
      >
        <Upload className="w-6 h-6 text-stone-500 group-hover:text-amber-400" />
        <span className="text-sm text-stone-300">Tap to upload — photos &amp; videos</span>
        <span className="text-[11px] text-stone-500">{imageCount}/{PHOTO_MAX} photos · min {PHOTO_MIN}</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        data-testid="gallery-file-input"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
      />

      {/* In-flight uploads */}
      {Object.entries(uploads).length > 0 && (
        <div className="space-y-1.5">
          {Object.entries(uploads).map(([id, u]) => (
            <div key={id} className="text-xs text-stone-300">
              <div className="flex justify-between"><span className="truncate">{u.name}</span><span>{u.error ? "Error" : `${u.pct}%`}</span></div>
              <div className="mt-1 h-1 bg-stone-800 rounded overflow-hidden">
                <div className={`h-full ${u.error ? "bg-rose-500" : "bg-amber-500"} transition-all`} style={{ width: `${u.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Gallery grid */}
      {value.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="gallery-grid">
          {value.map((g, i) => (
            <div
              key={`${g.url}-${i}`}
              draggable
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver}
              onDrop={onDrop(i)}
              className={`relative aspect-[3/4] rounded-lg overflow-hidden border bg-stone-900 group ${g.is_main ? "border-amber-500/70 ring-1 ring-amber-500/30" : "border-stone-700"}`}
              data-testid={`gallery-item-${i}`}
            >
              {g.type === "image" ? (
                <img src={resolveUrl(g.url)} onError={onImageError} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-stone-800">
                  <VideoIcon className="w-7 h-7 text-stone-400" />
                </div>
              )}
              {g.is_main && (
                <div className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-stone-950 font-semibold flex items-center gap-1">
                  <Star className="w-3 h-3" /> Main
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-1.5 flex items-center justify-between gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
                {g.type === "image" && !g.is_main && (
                  <button onClick={() => setMain(i)} className="p-1.5 rounded bg-stone-900/80 text-stone-100 hover:text-amber-400" data-testid={`gallery-main-${i}`} aria-label="Set as main">
                    <Star className="w-3.5 h-3.5" />
                  </button>
                )}
                {g.type === "image" && (
                  <button onClick={() => startCrop(i)} className="p-1.5 rounded bg-stone-900/80 text-stone-100 hover:text-amber-400" data-testid={`gallery-crop-${i}`} aria-label="Crop">
                    <Crop className="w-3.5 h-3.5" />
                  </button>
                )}
                <div className="flex items-center gap-0.5">
                  <button onClick={() => moveTo(i, -1)} disabled={i === 0} className="p-1.5 rounded bg-stone-900/80 text-stone-100 disabled:opacity-30" aria-label="Move up" data-testid={`gallery-up-${i}`}>
                    <GripVertical className="w-3.5 h-3.5 -rotate-90" />
                  </button>
                  <button onClick={() => moveTo(i, 1)} disabled={i === value.length - 1} className="p-1.5 rounded bg-stone-900/80 text-stone-100 disabled:opacity-30" aria-label="Move down" data-testid={`gallery-down-${i}`}>
                    <GripVertical className="w-3.5 h-3.5 rotate-90" />
                  </button>
                </div>
                <button onClick={() => remove(i)} className="p-1.5 rounded bg-stone-900/80 text-rose-300 hover:text-rose-400" data-testid={`gallery-remove-${i}`} aria-label="Remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-stone-500 text-center py-4 flex items-center justify-center gap-2">
          <ImageIcon className="w-4 h-4" /> No photos yet
        </p>
      )}

      {cropping && (
        <PhotoCropper
          src={cropping.src}
          onCancel={() => setCropping(null)}
          onCropped={finishCrop}
        />
      )}
    </div>
  );
}
